import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  CalendarMonth,
  LocalShipping,
  LocationOn,
  Person,
  Phone,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import ShareLeadWhatsAppModal from "../../leads/components/ShareLeadWhatsAppModal.jsx";
import BiddingPanel from "../../leads/bidding/components/BiddingPanel.jsx";
import {
  getOpportunity,
  qualifyOpportunity,
  getSuggestedProvidersForOpportunity,
} from "../services/opportunityService.js";
import { getLead } from "../../leads/services/leadService.js";
import {
  PageShell,
  PageHeader,
  PageSection,
} from "@/shared/components/pageShell";

const statusColor = (status) => {
  if (status === "qualified") return "success";
  if (status === "draft") return "default";
  return "info";
};

const formatMatchReason = (reason) =>
  String(reason || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getInventoryLines = (opportunity) => {
  const items = opportunity?.inventory?.items;
  if (Array.isArray(items) && items.length > 0) {
    return items
      .map((item) => {
        if (typeof item === "string") return item.trim();
        const content =
          item.content || item.description || item.caption || "Item";
        const quantity =
          item.quantity && item.quantity > 1 ? `${item.quantity}x ` : "";
        return `${quantity}${String(content).trim()}`;
      })
      .filter(Boolean);
  }

  const notes = opportunity?.inventory?.notes;
  if (!notes) return [];

  return notes
    .split(";")
    .map((line) => line.trim())
    .filter(Boolean);
};

const InventoryList = ({ lines }) => {
  const [expanded, setExpanded] = useState(false);
  const previewCount = 5;
  const hasMore = lines.length > previewCount;
  const visibleLines = expanded || !hasMore ? lines : lines.slice(0, previewCount);

  if (lines.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Inventory ({lines.length} {lines.length === 1 ? "item" : "items"})
      </Typography>
      <List dense disablePadding sx={{ listStyle: "disc", pl: 2.5 }}>
        {visibleLines.map((line, index) => (
          <ListItem
            key={`${line}-${index}`}
            disableGutters
            sx={{ display: "list-item", py: 0.25 }}
          >
            <ListItemText
              primary={line}
              primaryTypographyProps={{ variant: "body2", sx: { lineHeight: 1.5 } }}
            />
          </ListItem>
        ))}
      </List>
      {hasMore && (
        <Button
          size="small"
          onClick={() => setExpanded((value) => !value)}
          sx={{ mt: 0.5, textTransform: "none", px: 0 }}
        >
          {expanded ? "Show less" : `Show all ${lines.length} items`}
        </Button>
      )}
    </Box>
  );
};

const OpportunityDetailView = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [opportunity, setOpportunity] = useState(null);
  const [lead, setLead] = useState(null);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const inventoryLines = useMemo(
    () => getInventoryLines(opportunity),
    [opportunity]
  );

  const moveDateLabel = useMemo(() => {
    if (!opportunity?.move) return "TBC";
    return (
      opportunity.move.earliestMoveDate ||
      opportunity.move.moveDates?.join(", ") ||
      "TBC"
    );
  }, [opportunity]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const oppResponse = await getOpportunity(opportunityId);
      if (!oppResponse.success) {
        throw new Error(oppResponse.error || "Failed to load opportunity");
      }
      setOpportunity(oppResponse.data);

      if (oppResponse.data?.leadId) {
        const leadResponse = await getLead(oppResponse.data.leadId);
        if (leadResponse.success) {
          setLead(leadResponse.data);
        }
      }

      const matchResponse = await getSuggestedProvidersForOpportunity(
        opportunityId
      );
      if (matchResponse.success) {
        setMatches(matchResponse.matches || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load opportunity");
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleQualify = async () => {
    setActionLoading(true);
    try {
      const response = await qualifyOpportunity(opportunityId);
      if (response.success) {
        setOpportunity(response.data);
      } else {
        setError(response.error || "Failed to qualify opportunity");
      }
    } catch (err) {
      setError(err.message || "Failed to qualify opportunity");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !opportunity) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        spacing={{ xs: 2, md: 0 }}
        mb={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 1 }}
          >
            Opportunity
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              wordBreak: "break-all",
              lineHeight: 1.2,
            }}
          >
            {opportunity?.opportunityNumber || "Opportunity"}
          </Typography>
          <Chip
            size="small"
            label={opportunity?.status}
            sx={{ mt: 1, textTransform: "capitalize" }}
            color={statusColor(opportunity?.status)}
          />
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0 }}
        >
          {opportunity?.leadId && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/leads/${opportunity.leadId}`)}
              fullWidth
              sx={{ width: { xs: "100%", sm: "auto" }, textTransform: "none" }}
            >
              View Lead
            </Button>
          )}
          {opportunity?.status === "draft" && (
            <Button
              variant="contained"
              onClick={handleQualify}
              disabled={actionLoading}
              fullWidth
              sx={{ width: { xs: "100%", sm: "auto" }, textTransform: "none" }}
            >
              Qualify
            </Button>
          )}
          {lead && (
            <Button
              variant="contained"
              onClick={() => setShareOpen(true)}
              fullWidth
              sx={{ width: { xs: "100%", sm: "auto" }, textTransform: "none" }}
            >
              Share with providers
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <PageSection>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
          Move summary
        </Typography>

        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Person fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {opportunity?.customer?.name || "Unknown customer"}
            </Typography>
            {opportunity?.customer?.phone && (
              <>
                <Typography variant="body2" color="text.secondary">
                  ·
                </Typography>
                <Phone sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {opportunity.customer.phone}
                </Typography>
              </>
            )}
          </Stack>

          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.75 }}>
              <LocationOn fontSize="small" color="primary" sx={{ mt: 0.25 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  From
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {opportunity?.move?.movingFrom || "Not specified"}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LocationOn fontSize="small" color="secondary" sx={{ mt: 0.25 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  To
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {opportunity?.move?.movingTo || "Not specified"}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<LocalShipping sx={{ fontSize: "16px !important" }} />}
              label={`Corridor: ${opportunity?.move?.routeCorridor || "UNKNOWN"}`}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<CalendarMonth sx={{ fontSize: "16px !important" }} />}
              label={`Move: ${moveDateLabel}`}
              variant="outlined"
            />
          </Stack>
        </Stack>

        <InventoryList lines={inventoryLines} />
      </PageSection>

      <PageSection>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
          Suggested providers
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        {matches.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No route matches yet. Add coverage routes on partner provider profiles.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {matches.map((match) => (
              <Box
                key={match.partnerId}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  bgcolor: "background.default",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {match.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {match.phone}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`Score ${match.score}`}
                    color={match.score >= 4 ? "success" : "default"}
                    sx={{ flexShrink: 0 }}
                  />
                </Stack>
                {match.matchReasons?.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {match.matchReasons.map((reason) => (
                      <Chip
                        key={`${match.partnerId}-${reason}`}
                        size="small"
                        label={formatMatchReason(reason)}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </PageSection>

      {lead && opportunity?.leadId && (
        <PageSection sx={{ overflow: { xs: "hidden", sm: "visible" } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            Provider offers &amp; customer options
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Capture provider prices, build customer-facing options (A–D), preview
            and send all included options to the customer.
          </Typography>
          <BiddingPanel
            embedded
            leadId={opportunity.leadId}
            lead={lead}
            onLeadUpdated={loadData}
          />
        </PageSection>
      )}

      {lead && (
        <ShareLeadWhatsAppModal
          open={shareOpen}
          lead={lead}
          onClose={() => setShareOpen(false)}
          suggestedProviders={matches}
        />
      )}
    </PageShell>
  );
};

export default OpportunityDetailView;
